import React, { useState, useEffect } from "react";
import logo from "./Media/logo.jpg";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import "../App.css";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navItems = [
    { label: "Why MyWagePay", path: "/whymywagepay" },
    {
      label: "Products",
      items: [
        { label: "Embed", path: "/embed", description: "Integration solutions" },
        { label: "Capital", path: "/capital", description: "Financial services" },
        { label: "Score Check", path: "/profiling", description: "Credit profiling" },
      ],
    },
    {
      label: "Company",
      items: [
        { label: "About Us", path: "/aboutus", description: "Our story" },
        { label: "Careers", path: "/careers", description: "Join our team" },
        { label: "Contact Us", path: "/register", description: "Get in touch" },
      ],
    },
    { label: "Invest", path: "/invest" },
    {
      label: "Learn",
      items: [
        { label: "What's New", path: "/news", description: "Latest updates" },
        { label: "Blog", path: "/blog", description: "Insights & articles" },
      ],
    },
  ];

  const handleDropdownToggle = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const isActive = (path) => {
    return path === location.pathname;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&display=swap');

        .navbar-root {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .navbar-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .navbar-container.scrolled {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 8px 24px rgba(0, 0, 0, 0.03);
        }

        .navbar-container:not(.scrolled) {
          background: rgb(250, 249, 248);
        }

        .navbar-inner {
          max-width: 1800px;
          margin: 0 auto;
          padding: 0 2.5rem;
          height: 4.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          transition: transform 0.2s ease;
        }

        .nav-logo:hover {
          transform: translateY(-1px);
        }

        .nav-logo-image {
          width: 3rem;
          height: 3rem;
          border-radius: 0.5rem;
          object-fit: contain;
        }

        .nav-logo-text {
          font-family: 'Archivo', sans-serif;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a1a;
          letter-spacing: -0.02em;
        }

        .nav-desktop {
          display: none;
        }

        @media (min-width: 1024px) {
          .nav-desktop {
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }
        }

        .nav-item {
          position: relative;
          padding: 0.625rem 1rem;
          color: #374151;
          font-size: 0.9375rem;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.2s ease;
          cursor: pointer;
          border-radius: 0.5rem;
        }

        .nav-item:hover {
          color: #d97706;
        }

        .nav-item.active {
          color: #d97706;
          background: rgba(217, 119, 6, 0.08);
        }

        .nav-item-with-dropdown {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .nav-chevron {
          width: 1rem;
          height: 1rem;
          transition: transform 0.2s ease;
        }

        .nav-chevron.open {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: calc(100% + 0.5rem);
          left: 0;
          min-width: 15rem;
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06);
          padding: 0.5rem;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-8px);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .dropdown-menu.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: block;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .dropdown-item:hover {
          background: rgba(217, 119, 6, 0.08);
        }

        .dropdown-item-label {
          display: block;
          color: #1a1a1a;
          font-weight: 500;
          font-size: 0.9375rem;
          margin-bottom: 0.125rem;
        }

        .dropdown-item:hover .dropdown-item-label {
          color: #d97706;
        }

        .dropdown-item-description {
          display: block;
          color: #6b7280;
          font-size: 0.8125rem;
        }

        .mobile-menu-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.5rem;
          border: none;
          background: transparent;
          color: #1a1a1a;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .mobile-menu-button:hover {
          background: rgba(0, 0, 0, 0.05);
        }

        @media (min-width: 1024px) {
          .mobile-menu-button {
            display: none;
          }
        }

        .mobile-menu-overlay {
          position: fixed;
          inset: 0;
          z-index: 999;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(4px);
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .mobile-menu-overlay.open {
          opacity: 1;
          visibility: visible;
        }

        .mobile-menu {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          max-width: 28rem;
          z-index: 1000;
          background: white;
          box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
          transform: translateX(100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow-y: auto;
        }

        .mobile-menu.open {
          transform: translateX(0);
        }

        .mobile-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }

        .mobile-menu-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.5rem;
          border: none;
          background: transparent;
          color: #1a1a1a;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .mobile-menu-close:hover {
          background: rgba(0, 0, 0, 0.05);
        }

        .mobile-menu-content {
          padding: 1rem;
        }

        .mobile-nav-item {
          margin-bottom: 0.25rem;
        }

        .mobile-nav-link {
          display: block;
          padding: 0.875rem 1rem;
          color: #1a1a1a;
          font-weight: 500;
          font-size: 1rem;
          text-decoration: none;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
        }

        .mobile-nav-link:hover,
        .mobile-nav-link.active {
          background: rgba(217, 119, 6, 0.08);
          color: #d97706;
        }

        .mobile-dropdown-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.875rem 1rem;
          color: #1a1a1a;
          font-weight: 500;
          font-size: 1rem;
          text-align: left;
          border: none;
          background: transparent;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mobile-dropdown-trigger:hover {
          background: rgba(0, 0, 0, 0.03);
        }

        .mobile-dropdown-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .mobile-dropdown-content.open {
          max-height: 500px;
        }

        .mobile-dropdown-items {
          padding: 0.5rem 0 0.5rem 1rem;
        }

        .mobile-dropdown-link {
          display: block;
          padding: 0.75rem 1rem;
          color: #4b5563;
          font-size: 0.9375rem;
          text-decoration: none;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
        }

        .mobile-dropdown-link:hover {
          background: rgba(217, 119, 6, 0.08);
          color: #d97706;
        }

        @media (max-width: 640px) {
          .navbar-inner {
            padding: 0 1.25rem;
          }
        }
      `}</style>

      <nav className="navbar-root">
        <div className={`navbar-container ${scrolled ? "scrolled" : ""}`}>
          <div className="navbar-inner">
            {/* Logo */}
            <Link to="/" className="nav-logo">
              <img src={logo} alt="MyWagePay" className="nav-logo-image" />
              <span className="nav-logo-text">MyWagePay</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="nav-desktop">
              {navItems.map((item) => {
                if (item.items) {
                  return (
                    <div
                      key={item.label}
                      className="nav-item"
                      onMouseEnter={() => setActiveDropdown(item.label)}
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <div className="nav-item-with-dropdown">
                        <span>{item.label}</span>
                        <ChevronDown
                          className={`nav-chevron ${
                            activeDropdown === item.label ? "open" : ""
                          }`}
                        />
                      </div>
                      <div
                        className={`dropdown-menu ${
                          activeDropdown === item.label ? "open" : ""
                        }`}
                      >
                        {item.items.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.path}
                            to={dropdownItem.path}
                            className="dropdown-item"
                          >
                            <span className="dropdown-item-label">
                              {dropdownItem.label}
                            </span>
                            {dropdownItem.description && (
                              <span className="dropdown-item-description">
                                {dropdownItem.description}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`nav-item ${isActive(item.path) ? "active" : ""}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`mobile-menu-overlay ${mobileMenuOpen ? "open" : ""}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-menu-header">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="MyWagePay" className="nav-logo-image" />
              <span className="nav-logo-text">MyWagePay</span>
            </Link>
            <button
              className="mobile-menu-close"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          <div className="mobile-menu-content">
            {navItems.map((item) => {
              if (item.items) {
                return (
                  <div key={item.label} className="mobile-nav-item">
                    <button
                      className="mobile-dropdown-trigger"
                      onClick={() => handleDropdownToggle(item.label)}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`nav-chevron ${
                          activeDropdown === item.label ? "open" : ""
                        }`}
                        size={20}
                      />
                    </button>
                    <div
                      className={`mobile-dropdown-content ${
                        activeDropdown === item.label ? "open" : ""
                      }`}
                    >
                      <div className="mobile-dropdown-items">
                        {item.items.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.path}
                            to={dropdownItem.path}
                            className="mobile-dropdown-link"
                          >
                            {dropdownItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={item.label} className="mobile-nav-item">
                  <Link
                    to={item.path}
                    className={`mobile-nav-link ${
                      isActive(item.path) ? "active" : ""
                    }`}
                  >
                    {item.label}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;